import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import styled from "styled-components";

import Modal from "src/components/Modal";
import { colors } from "src/styles";

import { ExportDateRangeModalProps } from "./types";

const ExportDateRangeModal = ({
  isOpen,
  onClose,
  onSubmit,
  title = "Export",
  confirmText = "Export",
  cancelText = "Cancel",
  isSubmitting = false,
  initialFromDate,
  initialToDate,
  maxDate = new Date(),
}: ExportDateRangeModalProps) => {
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (!isOpen) return;

    setFromDate(initialFromDate || moment().subtract(30, "days").toDate());
    setToDate(initialToDate || new Date());
    setValidationError("");
  }, [isOpen, initialFromDate, initialToDate]);

  const onApproveClick: React.MouseEventHandler<HTMLButtonElement> = async (event) => {
    event.preventDefault();

    if (!fromDate || !toDate) {
      setValidationError("Please select both From and To dates.");
      return;
    }

    if (moment(fromDate).isAfter(toDate, "day")) {
      setValidationError("From date cannot be after To date.");
      return;
    }

    setValidationError("");

    const isSuccess = await onSubmit({
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
    });

    if (isSuccess) {
      onClose();
    }
  };

  return (
    <Modal
      isShowing={isOpen}
      hide={onClose}
      title={title}
      onCancelClick={onClose}
      onApproveClick={onApproveClick}
      approveButtonText={confirmText}
      cancelButtonText={cancelText}
      approveButtonStyle="primary"
      cancelButtonStyle="secondary"
      disableApproveButton={isSubmitting}
      isLoadingApproveButton={isSubmitting}
    >
      <FieldWrapper>
        <FieldLabel>From Date</FieldLabel>
        <StyledDatePicker
          selected={fromDate}
          onChange={(date: Date | null) => setFromDate(date)}
          dateFormat="MM/dd/yyyy"
          maxDate={toDate || maxDate}
          placeholderText="Select from date"
        />
      </FieldWrapper>

      <FieldWrapper>
        <FieldLabel>To Date</FieldLabel>
        <StyledDatePicker
          selected={toDate}
          onChange={(date: Date | null) => setToDate(date)}
          dateFormat="MM/dd/yyyy"
          minDate={fromDate || undefined}
          maxDate={maxDate}
          placeholderText="Select to date"
        />
      </FieldWrapper>

      {validationError ? <ValidationError>{validationError}</ValidationError> : null}
    </Modal>
  );
};

const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.grey.dark};
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  padding: 8px 10px;
  font-size: 14px;
  border: 1px solid ${colors.grey.light11};
  border-radius: 4px;
  outline: none;

  &:focus {
    border-color: ${colors.kleenway.green};
  }
`;

const ValidationError = styled.div`
  color: #d32f2f;
  font-size: 13px;
  font-weight: 500;
`;

export default ExportDateRangeModal;
